import { usePageViewTimer } from "@shared/hooks/usePageViewTimer";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { Tabs } from "@shared/ui/tabs"; // Your URL-based tabs component
import {
  ArrowLeft,
  BarChart3,
  Camera,
  CheckCircle,
  CheckCircle2,
  ClipboardList,
  Copy,
  Eye,
  FileX2,
  Settings2,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { ValidationContextProvider } from "../../context";
import {
  useValidationBreakList,
  useValidationBreakRunCount,
} from "../../hooks";
import DuplicateReadingLanding from "./DuplicateReading/DuplicateReadingLanding";
import ExemptionsLanding from "./Exemptions/ExemptionsLanding";
import MissingReadingLanding from "./MissingReading/MissingReadingLanding";
import NewReadingLanding from "./NewReadings/NewReadingLanding";
import PassLanding from "./PassReading/PassLanding";
import PendingReadingLanding from "./PendingReading/PendingReadingLanding";
import RevisitLanding from "./RevisitReading/RevisitLanding";
import ViewReadingLanding from "./ViewReading/ViewReadingLanding";
import { CustomTooltip } from "@shared/components/CustomTooltip";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { useState } from "react";
import { Separator } from "@shared/ui/separator";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import ManageAssignmentDialog from "../ValidationDashboard/ManageAssignmentDialog";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
} from "@shared/ui/accordion";
import { AccordionContent } from "@radix-ui/react-accordion";
import NavigateToReading from "../shared/NavigateToReading";
import HowValidationWorksDialog from "../shared/HowValidationWorksDialog";
import {
  useAuditUsers,
  usePostMeterCycleAuditUserMapping,
  usePutMeterCycleAuditUserMapping,
} from "@features/mx/validations/hooks";
import { logEvent } from "@shared/analytics/analytics";
import { PAGE_SIZE } from "@shared/utils/constants";
import { toast } from "@shared/hooks/use-toast";
import { useRunValidations } from "@features/mx/meterReads/hooks";
import { useGetRules } from "@features/mx/readCycleManagement/hooks";
import AutoValidateDialog from "../shared/AutoValidateDialog";


function PhotoReadsValidationDashboard() {
  const remoteUtilityId = useRemoteUtilityId();
  const navigate = useNavigate();
  const { meterRunId } = useParams<{ meterRunId: string }>();
  const runId = Number(meterRunId);
  const { data: meterCount } = useValidationBreakRunCount(
    meterRunId,
    1,
    10,
    remoteUtilityId
  );

  const { data: activeCycles, isFetching } = useValidationBreakList({
    remoteUtilityId,
    page: 1,
    limit: PAGE_SIZE,
  });
const { data: estimationRulesData } = useGetRules(1, true);
  const cycle = activeCycles?.results?.find(
    (cycle) => cycle.runId === runId
  );
  const dueIn = cycle?.dueIn;
  const validatorNames =
    cycle?.validatorInfo?.map((v) => v.validatorName).filter(Boolean) || [];

  const supervisorNames =
    cycle?.supervisorInfo?.map((s) => s.supervisorName).filter(Boolean) || [];
  
  // ✅ Dialog state
  const [openDialog, setOpenDialog] = useState<
    null | "validator" | "supervisor"
  >(null);
  const [openHowWorks, setOpenHowWorks] = useState(false);
  const [auditUserSearch, setAuditUserSearch] = useState("");

  const userRoleType = openDialog === "validator" ? 0 : openDialog === "supervisor" ? 2 : 0; // 0 for validator | 2 for supervisor
  // ✅ Hooks for fetching audit users
  const { data: validatorsList } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog === "validator",
    cycle?.cycleId,
    auditUserSearch
  );
  const { data: supervisorList } = useAuditUsers(
    remoteUtilityId,
    userRoleType,
    openDialog === "supervisor",
    cycle?.cycleId,
    auditUserSearch
  );

  const {mutate: runPendingValidations} = useRunValidations();
  const [isValidateDialogOpen, setIsValidateDialogOpen] = useState(false);
  const handleAutoValidate = (photoValidationAction: boolean, options?: { onSuccess?: () => void }) => {
  runPendingValidations(
    {
      payload: {
        meterRunId,
        photo_validation_action: photoValidationAction,
      },
    },
    {
      onSuccess: () => {
        toast({
          title: photoValidationAction
            ? "Auto-validation started. Readings kept for photo validation."
            : "Auto-validation started. Readings passed directly.",
          description:
            "Validations are running in the background. Please check the Validations list after some time.",
        });
        options?.onSuccess?.();
      },
    }
  );
};

  // ✅ Mutations for assign/unassign
  const postMeterCycleAuditUserMappingMutation =
    usePostMeterCycleAuditUserMapping(userRoleType, cycle?.cycleId);
  const putMeterCycleAuditUserMappingMutation =
    usePutMeterCycleAuditUserMapping(userRoleType, cycle?.cycleId);

  usePageViewTimer({
    pageName: "Validations Detail Page",
    extraProperties: {
      meterRunId: meterRunId,
    },
  });  
  // Configure tabs for URL-based navigation
  const tabComponents = {
    pending: {
      label: "Pending (V1)",
      shortLabel: "Photos",
      icon: <Camera size={16} />,
      component: <PendingReadingLanding />,
      count: meterCount?.newCount,
    },
    exemptions: {
      label: "Exemptions (V2)",
      shortLabel: "Reports",
      icon: <BarChart3 size={16} />,
      component: <ExemptionsLanding />,
      count: meterCount?.exemptionCount,
    },
    missing: {
      label: "Missing Readings",
      shortLabel: "Valid",
      icon: <CheckCircle size={16} />,
      component: <MissingReadingLanding />,
      count: meterCount?.pendingCount,
    },
    revisit: {
      label: "Revisit",
      shortLabel: "Reports",
      icon: <BarChart3 size={16} />,
      component: <RevisitLanding />,
      count: meterCount?.revisitCount,
    },
    new: {
      label: "Ghost Meters",
      shortLabel: "Info",
      icon: <Eye size={16} />,
      component: <NewReadingLanding />,
      count: meterCount?.newMeterCount,
    },
    pass: {
      label: "Pass",
      shortLabel: "Reports",
      icon: <BarChart3 size={16} />,
      component: <PassLanding />,
      count: meterCount?.passCount,
    },
  };
  
  // Optional: URL mapping for cleaner URLs
  const urlMapping = {
    new: "new-readings",
    pending: "pending-readings",
    missing: "missing-readings",
    exemptions: "exemptions",
  };

  const validationKpiCards = [
    {
      title: "Total Readings",
      count: cycle?.totalReadingCount ?? 0,
      trendSummary: "Overall count of resources scheduled for review",
      icon: <BarChart3 className="h-8 w-8 text-muted-foreground" />,
      tooltip: "All readings in this read cycle.",
    },
    {
      title: "Collected",
      count: cycle?.collectedCount ?? 0,
      trendSummary: "Inbound submissions awaiting or after review",
      icon: <ClipboardList className="h-8 w-8 text-blue-500" />,
      tooltip: "Received from field teams (includes validated).",
    },
    {
      title: "Validated",
      count: cycle?.pass ?? 0,
      trendSummary: "Passed verification and quality checks",
      icon: <CheckCircle2 className="h-8 w-8 text-green-600" />,
      tooltip: "Readings reviewed and marked as valid.",
    },
    {
      title: "Exemptions",
      count: cycle?.exemptedCount ?? 0,
      trendSummary: "Excluded per policy or exception handling",
      icon: <FileX2 className="h-8 w-8 text-red-600" />,
      tooltip: "Excluded due to business rules.",
    },
  ];

  // ✅ Assign handler
  const handleAssign = (row: any) => {
    const payload = {
      meterCycle: cycle?.cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    postMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validator Added", payload);
      },
    });
  };

  // ✅ Unassign handler
  const handleUnassign = (row: any) => {
    const payload = {
      meter_cycle: cycle?.cycleId,
      audit_user: row?.id,
      remote_utility_id: remoteUtilityId,
    };
    putMeterCycleAuditUserMappingMutation.mutate(payload, {
      onSuccess: () => {
        logEvent("MX Validator Removed", payload);
      },
    });
  };

  return (
    <ValidationContextProvider cycleId={cycle?.cycleId}>
      <div className="py-4 px-0 space-y-4">
        <Button 
        variant="outline" 
        onClick={() => {navigate(`/mx/meter-validation`);}}
        ><ArrowLeft/> Back</Button>
        {/* Header Information */}
        <Accordion
          type="single"
          collapsible
          defaultValue="item-1"
          className="px-0"
        >
          <AccordionItem value="item-1" className="border-2 rounded-lg px-0">
            <AccordionTrigger className="px-3 pt-6 pb-3 hover:no-underline">
              <div className="w-full flex justify-between items-center">
                <div className="flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    <div className="text-xl md:text-2xl font-bold tracking-tight">
                      {cycle?.meterCycleName}
                    </div>
                    <Badge variant="secondary">{cycle?.meterType}</Badge>
                    {cycle?.productCode && (
                      <Badge variant="outline">{cycle?.productCode[0]}</Badge>
                    )}
                    {/* {cycle?.meterType && (
                      <Badge variant="outline">{cycle?.meterType}</Badge>
                    )} */}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-start gap-2">
                    <span>
                      Reading Period: {cycle?.createdDate} –{" "}
                      {cycle?.validationEnd}
                    </span>
                    <Separator
                      orientation="vertical"
                      className="h-4 hidden md:inline-block"
                    />
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs
              ${
                dueIn > 7
                  ? "border-green-200 bg-green-50 text-green-700"
                  : dueIn >= 1
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : dueIn === 0
                  ? "border-blue-200 bg-blue-50 text-blue-700"
                  : "border-red-200 bg-red-50 text-red-700"
              }
            `}
                      aria-label="Due status"
                      title={
                        dueIn > 0
                          ? `This cycle ends in ${dueIn} day${
                              dueIn === 1 ? "" : "s"
                            }`
                          : dueIn === 0
                          ? "This cycle is due today"
                          : `This cycle is past due by ${Math.abs(dueIn)} day${
                              Math.abs(dueIn) === 1 ? "" : "s"
                            }`
                      }
                    >
                      {dueIn > 0
                        ? `Due in ${dueIn} day${dueIn === 1 ? "" : "s"}`
                        : dueIn === 0
                        ? "Due today"
                        : `Past due by ${Math.abs(dueIn)} day${
                            Math.abs(dueIn) === 1 ? "" : "s"
                          }`}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 mr-2">
                  <Button
                  disabled={estimationRulesData?.length === 0}
                    variant="default"
                    size="sm"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsValidateDialogOpen(true);}}
                  >
                    Auto Validate
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenHowWorks(true);
                    }}
                  >
                    How Validation Works
                  </Button>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 pt-0">
              <div className="space-y-4">
                {/* KPI tiles grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {validationKpiCards.map((card, index) => (
                    <QuickStatsCard
                      key={index}
                      title={card.title}
                      count={card.count}
                      trendSummary={card.trendSummary}
                      icon={card.icon}
                      tooltip={card.tooltip}
                    />
                  ))}
                </div>

                {/* People tiles */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {/* Validators */}
                  <div className="rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        Validators
                        <CustomTooltip message="People who validated readings in this cycle." />
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOpenDialog("validator")}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="mt-1 text-sm flex items-center gap-1">
                      {validatorNames.length === 0 ? (
                        <span className="text-muted-foreground">N/A</span>
                      ) : (
                        <>
                          <span className="font-medium">
                            {validatorNames.slice(0, 2).join(", ")}
                            {validatorNames.length > 2 &&
                              ` +${validatorNames.length - 2}`}
                          </span>
                          <CustomTooltip message={validatorNames.join(", ")} />
                        </>
                      )}
                    </div>
                  </div>

                  {/* Supervisors */}
                  <div className="rounded-md border p-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        Supervisors
                        <CustomTooltip message="People who supervised this validation cycle." />
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setOpenDialog("supervisor")}
                      >
                        <Settings2 className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="mt-1 text-sm flex items-center gap-1">
                      {supervisorNames.length === 0 ? (
                        <span className="text-muted-foreground">N/A</span>
                      ) : (
                        <>
                          <span className="font-medium">
                            {supervisorNames.slice(0, 2).join(", ")}
                            {supervisorNames.length > 2 &&
                              ` +${supervisorNames.length - 2}`}
                          </span>
                          <CustomTooltip message={supervisorNames.join(", ")} />
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Guidance */}
                <div className="text-xs text-muted-foreground">
                  Tip: Use the tabs below to filter by status. Use the search
                  box to find a specific resource number quickly.
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <NavigateToReading />
        {/* Tabs Section */}
        <Tabs
          defaultValue="pending"
          urlMapping={urlMapping}
          tabComponents={tabComponents}
          tabsListFullWidth={true}
          idPrefix="validation-tab"
          tabsListClassName="grid w-full grid-cols-6 mb-6"
        />
        <HowValidationWorksDialog
          openHowWorks={openHowWorks}
          setOpenHowWorks={setOpenHowWorks}
        />

        {openDialog && (
          <ManageAssignmentDialog
            open={!!openDialog}
            onOpenChange={(open) => !open && setOpenDialog(null)}
            type={openDialog}
            data={openDialog === "validator" ? validatorsList : supervisorList}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            onSearch={(search) => {
              setAuditUserSearch(search);
            }}
          />
        )}

      <AutoValidateDialog
        isValidationDialogOpen={isValidateDialogOpen}
        setIsValidationDialogOpen={setIsValidateDialogOpen}
        onStartValidation={handleAutoValidate}
      />      </div>
    </ValidationContextProvider>
  );
}
export default PhotoReadsValidationDashboard;